import {
  IconClock,
  IconHourglass,
  IconX,
  IconChecks,
} from "@tabler/icons-react"
import { CertificateStatus } from "@tfg/db/browser"
import { BadgeVariant, StatusBadge } from "@tfg/ui"
import { useTranslation } from "react-i18next"
import type { ReactNode } from "react"

type Props = {
  status: CertificateStatus | null
  rightSection?: ReactNode
  displayMode?: "default" | "lifecycle"
}

export function CertificateStatusBadge({
  status,
  rightSection,
  displayMode = "default",
}: Props) {
  const certificateStatusDetails = useGetCertificateStatus(status, displayMode)
  if (!certificateStatusDetails) return null
  return (
    <StatusBadge
      size="sm"
      text={certificateStatusDetails.translation}
      icon={certificateStatusDetails.icon}
      variant={certificateStatusDetails.variant}
      iconSize={14}
      rightSection={rightSection}
    />
  )
}

function useGetCertificateStatus(
  status: CertificateStatus | null,
  displayMode: "default" | "lifecycle"
) {
  const { t } = useTranslation("certificates")

  switch (status) {
    case CertificateStatus.PENDING:
      return {
        variant: BadgeVariant.WARNING,
        icon: displayMode === "lifecycle" ? null : <IconClock size={16} />,
        translation:
          displayMode === "lifecycle"
            ? t("certificateStatusLifecycle.PENDING")
            : t("certificateStatus.PENDING"),
      }
    case CertificateStatus.PROCESSED:
      return {
        variant: BadgeVariant.SUCCESS,
        icon: displayMode === "lifecycle" ? null : <IconChecks size={12} />,
        translation:
          displayMode === "lifecycle"
            ? t("certificateStatusLifecycle.PROCESSED")
            : t("certificateStatus.PROCESSED"),
      }
    case CertificateStatus.REJECTED:
      return {
        variant: BadgeVariant.ERROR,
        icon: displayMode === "lifecycle" ? null : <IconX size={16} />,
        translation:
          displayMode === "lifecycle"
            ? t("certificateStatusLifecycle.REJECTED")
            : t("certificateStatus.REJECTED"),
      }
    case CertificateStatus.NOT_UPLOADED:
      return {
        variant: BadgeVariant.INFO,
        icon: displayMode === "lifecycle" ? null : <IconHourglass size={16} />,
        translation:
          displayMode === "lifecycle"
            ? t("certificateStatusLifecycle.NOT_UPLOADED")
            : t("certificateStatus.NOT_UPLOADED"),
      }
    default:
      return null
  }
}
